var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useLocation, useNavigate } from 'react-router-dom';
import { match, compile } from 'path-to-regexp';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UXPContext } from '@core/context/Context';
import { getDashboardDetails } from '@core/services/services';
/** Internal hook — not for use outside the App component. */
export function useRoutes() {
    const context = useContext(UXPContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [dashboardConfig, setDashboardConfig] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const sortRoutes = useCallback((paths, direction = 'desc') => {
        return paths.sort((a, b) => {
            const aSegments = a.split('/').filter(Boolean);
            const bSegments = b.split('/').filter(Boolean);
            const segmentDiff = aSegments.length - bSegments.length;
            if (segmentDiff !== 0) {
                return direction === 'asc' ? segmentDiff : -segmentDiff;
            }
            // Fewer dynamic segments = more specific match
            const aParamCount = aSegments.filter(s => s.startsWith(':')).length;
            const bParamCount = bSegments.filter(s => s.startsWith(':')).length;
            return aParamCount - bParamCount;
        });
    }, []);
    const normalizeRoutes = useCallback((routes) => {
        // Strip query strings from route keys — path-to-regexp treats ? as an optional
        // parameter marker and throws if it appears in a route pattern.
        const sanitized = {};
        for (const [path, config] of Object.entries(routes)) {
            sanitized[path.split('?')[0]] = config;
        }
        const sortedPaths = sortRoutes(Object.keys(sanitized), 'asc');
        const normalized = {};
        for (const path of sortedPaths) {
            const segments = path.split('/').filter(Boolean);
            let inheritedGroups = [];
            let inheritedRoles = [];
            let parentPath = '';
            for (const segment of segments.slice(0, -1)) {
                parentPath += `/${segment}`;
                const parent = normalized[parentPath];
                if (parent) {
                    inheritedGroups = [...new Set([...inheritedGroups, ...(parent.userGroups || [])])];
                    inheritedRoles = [...new Set([...inheritedRoles, ...(parent.appRoles || [])])];
                }
            }
            const current = sanitized[path];
            normalized[path] = Object.assign(Object.assign({}, current), { userGroups: [...new Set([...(current.userGroups || []), ...inheritedGroups])], appRoles: [...new Set([...(current.appRoles || []), ...inheritedRoles])] });
        }
        return normalized;
    }, [sortRoutes]);
    const allRoutes = useMemo(() => normalizeRoutes(context.routes || {}), [context.routes]);
    const isDashboard = useMemo(() => {
        return location.pathname.startsWith('/dashboard');
    }, [location.pathname]);
    const fetchDashboardConfig = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!context || !(location === null || location === void 0 ? void 0 : location.pathname))
            return;
        setLoadingDashboard(true);
        /*
         * Dashboard URLs: /dashboard/<id>[/<subId>]
         */
        const parts = location.pathname.split('/').filter(Boolean);
        const dashboardId = parts[1];
        const subDashboardId = parts.slice(2).join('/') || 'default';
        if (!dashboardId) {
            setDashboardConfig(null);
            setLoadingDashboard(false);
            return;
        }
        const details = yield getDashboardDetails(context, dashboardId, subDashboardId);
        if (!details) {
            setDashboardConfig(null);
            setLoadingDashboard(false);
            return;
        }
        details.subDashboardId = subDashboardId;
        setDashboardConfig({
            title: ((_a = details === null || details === void 0 ? void 0 : details.details) === null || _a === void 0 ? void 0 : _a.Title) || 'Dashboard',
            pageId: '__dashboard__',
            appRoles: [],
            userGroups: ((_b = details === null || details === void 0 ? void 0 : details.details) === null || _b === void 0 ? void 0 : _b.ParsedRoles) || [],
            parameters: [],
            redirectTo: null,
            dashboarConfig: details,
        });
        setLoadingDashboard(false);
    }), [location === null || location === void 0 ? void 0 : location.pathname, UXPContext]);
    useEffect(() => {
        if (!isDashboard) {
            setDashboardConfig(null);
            return;
        }
        fetchDashboardConfig();
    }, [isDashboard, fetchDashboardConfig]);
    const matchedRoute = useMemo(() => {
        var _a, _b;
        const { pathname, search } = location;
        const query = Object.fromEntries(new URLSearchParams(search.replace(/^\?/, '')));
        // Only block rendering on the initial load (no existing config yet).
        // When navigating between sub-dashboards, keep the stale config visible
        // instead of returning null — returning null causes App to render
        // <BaseLayoutSkeleton /> which unmounts the entire tree on every link click.
        if (isDashboard && loadingDashboard && !dashboardConfig)
            return null;
        if (isDashboard && dashboardConfig) {
            return { matchedPath: pathname, config: dashboardConfig, params: null, query };
        }
        const sortedPaths = sortRoutes(Object.keys(allRoutes), 'desc');
        for (const path of sortedPaths) {
            const matched = match(path, { decode: decodeURIComponent })(pathname);
            if (matched) {
                let config = allRoutes[path];
                let params = matched.params || {};
                /*
                 * Symlink: transparently serve the target route's content at this URL.
                 * Explicit params stored on the symlink override URL-extracted params.
                 */
                if (config.type === 'symlink' && ((_b = (_a = config.metadata) === null || _a === void 0 ? void 0 : _a.symlink) === null || _b === void 0 ? void 0 : _b.target)) {
                    try {
                        const resolvedParams = Object.assign(Object.assign({}, params), (config.metadata.symlink.params || {}));
                        const targetUrl = compile(config.metadata.symlink.target)(resolvedParams);
                        const sortedForTarget = sortRoutes(Object.keys(allRoutes), 'desc');
                        for (const targetPath of sortedForTarget) {
                            const targetMatch = match(targetPath, { decode: decodeURIComponent })(targetUrl);
                            if (targetMatch) {
                                config = Object.assign(Object.assign({}, allRoutes[targetPath]), { type: 'symlink' });
                                params = resolvedParams;
                                break;
                            }
                        }
                    }
                    catch ( /* params missing — render symlink route as-is */_c) { /* params missing — render symlink route as-is */ }
                }
                return { matchedPath: path, config, params, query };
            }
        }
        return null;
    }, [location, allRoutes, sortRoutes, isDashboard, dashboardConfig, loadingDashboard]);
    // Anonymous = no real user session. Use the immutable isPublicSession flag set at context init,
    // not userKey/apiKey which mutate after setPublicCredentials fires for public dashboards.
    const isAnonymous = context.isPublicSession;
    const isAllowed = useMemo(() => {
        var _a;
        if (isAnonymous) {
            // Anonymous users can only access routes explicitly marked isPublic.
            // Dashboard routes use portal-level IsPublic — handled after portal loads.
            if (isDashboard)
                return true; // DashboardView will enforce portal's IsPublic
            return !!((_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.isPublic);
        }
        // Backend filters routes to only those the user can access before sending them.
        // Any route present in the config is already allowed for this user.
        return !!matchedRoute;
    }, [matchedRoute, isAnonymous, isDashboard]);
    /*
     * Handle redirects. Checks config.redirectTo (set by the server) and
     * config.metadata.redirect.to (set when the nav link type is 'redirect').
     */
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        const redirectTo = (_b = (_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.redirectTo) !== null && _b !== void 0 ? _b : (_e = (_d = (_c = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _c === void 0 ? void 0 : _c.metadata) === null || _d === void 0 ? void 0 : _d.redirect) === null || _e === void 0 ? void 0 : _e.to;
        if (redirectTo && isAllowed)
            navigate(redirectTo, { replace: true });
    }, [matchedRoute, isAllowed, navigate]);
    // isLoading drives <BaseLayoutSkeleton /> in App — only signal it on the initial
    // dashboard load (no existing config). Sub-dashboard navigations should not
    // unmount the layout; DashboardView handles its own per-content loading.
    return { matchedRoute, isAllowed, isDashboard, isLoading: loadingDashboard && !dashboardConfig, isAnonymous };
}
